/**
 * Phase 22 Plan 22-03 — DeactivateClientConfirmDialog
 *
 * Confirms soft-deactivation of a client via POST /api/clients/:id/deactivate.
 */

import { type JSX, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeactivateClientMutation } from '@/hooks/useClientQueries';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';

interface DeactivateClientConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly clientId: number;
  readonly clientName: string;
}

export function DeactivateClientConfirmDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
}: DeactivateClientConfirmDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deactivateMutation = useDeactivateClientMutation();

  function handleConfirm(): void {
    setErrorMessage(null);
    deactivateMutation.mutate(clientId, {
      onSuccess: () => {
        toast.success(`Client "${clientName}" deactivated`);
        onOpenChange(false);
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? (err.detail ?? err.message) : 'Unexpected error';
        setErrorMessage(msg);
        toast.error('Could not deactivate client', { description: msg });
      },
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate Client</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to deactivate <strong>{clientName}</strong>? This client will no
            longer appear in active lists. Work orders linked to this client are unaffected.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorMessage !== null && (
          <div
            className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="deactivate-client-error"
          >
            {errorMessage}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deactivateMutation.isPending}
            data-testid="deactivate-client-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={deactivateMutation.isPending}
            data-testid="deactivate-client-confirm"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
